/**
 * Auth API hooks (TanStack Query). The session is a cookie, so these just call the API; `useMe`
 * resolves the current principal (or null when unauthenticated) and is the source of truth for the
 * AuthProvider. Mutations prime/clear the `me` cache so the UI updates immediately.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiFetch } from "./client";

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  kind: "user" | "admin";
}

export const ME_KEY = ["me"] as const;

/** Current principal, or null if not authenticated (a 401 resolves to null, not an error). */
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async (): Promise<AuthUser | null> => {
      try {
        return (await apiFetch<{ user: AuthUser }>("/api/auth/me")).user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useValidateLink() {
  return useMutation({
    mutationFn: (token: string) =>
      apiFetch<{ valid: boolean; label?: string; expiresAt?: string }>("/api/auth/validate", {
        method: "POST",
        body: { token },
      }),
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string; email: string; password: string; displayName?: string }) =>
      apiFetch<{ user: AuthUser }>("/api/auth/register", { method: "POST", body: input }),
    onSuccess: (data) => qc.setQueryData(ME_KEY, data.user),
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string; email: string; password: string }) =>
      apiFetch<{ user: AuthUser }>("/api/auth/login", { method: "POST", body: input }),
    onSuccess: (data) => qc.setQueryData(ME_KEY, data.user),
  });
}

export function useAdminLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string; totp: string }) =>
      apiFetch<{ admin: AuthUser }>("/api/admin/login", { method: "POST", body: input }),
    onSuccess: (data) => qc.setQueryData(ME_KEY, data.admin),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: true }>("/api/auth/logout", { method: "POST" }),
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      qc.removeQueries({ queryKey: ["runs"] });
      qc.removeQueries({ queryKey: ["admin"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      apiFetch<{ ok: true }>("/api/auth/change-password", { method: "POST", body: input }),
  });
}
